import { useIntl } from 'react-intl';

import { PaymentMethodsLogo } from '@/components/ShoppingBag/PaymentMethodsLogo';
import { formatCheckoutOrder } from '@/components/TransactionSummary/TransactionSummary.utils';
import type { ContentfulPlaceholderCheckoutPaymentsFragment } from '@/graphql/contentful/api';
import { useCheckout } from '@/hooks/useCheckout';
import { useNativeCall } from '@/hooks/useNativeCall';
import { ProductTypeSlug } from '@/lib/const';
import { messages } from '@/messages/checkoutPaymentsPlaceholder';
import { useRegionProvider } from '@/providers/RegionProvider';

import * as S from './CheckoutPaymentsPlaceholder.styles';

export type itemType = {
  __typename?: 'Asset';
  contentType?: string | null;
  height?: number | null;
  sys: { __typename?: 'Sys'; id: string };
  title?: string | null;
  url?: string | null;
  width?: number | null;
} | null;

export const CheckoutPaymentsPlaceholder = ({
  ctaText,
}: ContentfulPlaceholderCheckoutPaymentsFragment) => {
  const { formatMessage } = useIntl();
  const { country } = useRegionProvider();
  const { initiateNativeCall } = useNativeCall(country.code);
  const {
    snapshot: { checkout, preOrderData },
  } = useCheckout();
  if (!checkout) return null;
  const { grossTotalPrice } = formatCheckoutOrder(checkout);
  const hasWatch = !!checkout?.lines?.some(
    (line) => line.variant?.product.productType.name === ProductTypeSlug.Watch,
  );
  return (
    <S.Wrapper data-test-id='CheckoutPaymentsPlaceholderWrapper'>
      <S.PaymentsBox data-test-id='CheckoutPaymentsPlaceholderPaymentsBox'>
        <S.PaymentsHeader
          data-test-id='CheckoutPaymentsPlaceholderPaymentsHeader'
          scale='h5'
          textTransform='uppercase'
        >
          {formatMessage(messages.paymentsHeader)}
        </S.PaymentsHeader>
        <S.ShopBagCardsListWrapper data-test-id='CheckoutPaymentsPlaceholderShopBagCardsListWrapper'>
          <PaymentMethodsLogo
            billingAddressCountryCode={checkout?.billingAddress?.country.code}
            hasWatch={hasWatch}
            id={checkout?.id}
            isDepositPaid={!!preOrderData?.deposit}
            isPreOrder={!!preOrderData}
            totalPrice={grossTotalPrice?.amount}
          />
        </S.ShopBagCardsListWrapper>
        <S.PaymentsSmallText data-test-id='CheckoutPaymentsPlaceholderPaymentsSmallText'>
          {formatMessage(messages.paymentsSmallText)}
        </S.PaymentsSmallText>
        <S.WhiteSpaceVertical data-test-id='CheckoutPaymentsPlaceholderWhiteSpaceVertical' />
      </S.PaymentsBox>
      <S.ContactsBox data-test-id='CheckoutPaymentsPlaceholderContactsBox'>
        <S.ContactsHeader
          data-test-id='CheckoutPaymentsPlaceholderContactsHeader'
          scale='h5'
          textTransform='uppercase'
        >
          {formatMessage(messages.contactsHeader)}
        </S.ContactsHeader>
        <S.ContactsText
          data-test-id='CheckoutPaymentsPlaceholderContactsText'
          scale='p2'
        >
          {formatMessage(messages.contactsText)}
        </S.ContactsText>
        <S.Cta
          colorVariant='white'
          data-test-id='CheckoutPaymentsPlaceholderCta'
          data-testid='cta'
          label={ctaText ?? formatMessage(messages.ctaLabel)}
          onClick={initiateNativeCall}
          variant='filled'
        />
      </S.ContactsBox>
    </S.Wrapper>
  );
};

CheckoutPaymentsPlaceholder.displayName = 'CheckoutPaymentsPlaceholder';
